'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Clock, Calendar } from 'lucide-react';

export default function DollarDominanceEMPage() {
  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: 'var(--background)', color: 'var(--foreground)' }}
    >
      <header
        className="sticky top-0 z-50 flex items-center justify-between px-6 md:px-12 lg:px-20"
        style={{
          height: '72px',
          backgroundColor: 'rgba(13,26,58,0.95)',
          borderBottom: '1px solid rgba(30,48,104,0.6)',
          backdropFilter: 'blur(12px)',
        }}
      >
        <Link
          href="/#insights"
          className="flex items-center gap-2 text-sm font-medium"
          style={{ color: 'var(--muted-foreground)' }}
        >
          <ArrowLeft size={15} />
          Back to Perspectives
        </Link>
        <div className="flex items-center gap-2">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/assets/macro-capital-logo.svg" alt="Macro Capital" style={{ width: '36px', height: '36px' }} />
          <span className="text-sm font-bold tracking-widest uppercase" style={{ color: 'var(--foreground)' }}>
            Macro Capital
          </span>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-16">
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <span
            className="text-[10px] font-semibold tracking-[0.18em] uppercase px-3 py-1 rounded-full"
            style={{
              backgroundColor: 'rgba(168,85,247,0.08)',
              border: '1px solid rgba(168,85,247,0.2)',
              color: '#c084fc',
            }}
          >
            Global Macro
          </span>
          <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <Calendar size={11} /> January 2025
          </span>
          <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <Clock size={11} /> 7 min read
          </span>
        </div>

        <h1
          className="font-semibold leading-tight mb-6"
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
            color: 'var(--foreground)',
          }}
        >
          Dollar Dominance and the Emerging Market Opportunity
        </h1>

        <p className="text-lg leading-relaxed mb-10" style={{ color: 'var(--muted-foreground)' }}>
          A structurally weaker dollar environment opens selective windows in emerging market equities and commodities. We map the opportunity set — and the risks that investors must navigate carefully.
        </p>

        <div className="h-px mb-10" style={{ backgroundColor: 'var(--border)' }} />

        <div className="space-y-8" style={{ color: 'var(--muted-foreground)', lineHeight: '1.85' }}>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              The Dollar's Structural Position
            </h2>
            <p className="mb-4">
              The US dollar's role as the world's reserve currency is one of the most consequential facts in global finance. It means that dollar-denominated assets — US Treasuries, dollar-priced commodities, dollar-invoiced trade — are the default safe haven in times of stress. It means that dollar strength tightens financial conditions globally, even in countries that have no direct economic relationship with the United States. And it means that dollar weakness, conversely, is a powerful tailwind for risk assets worldwide.
            </p>
            <p className="mb-4">
              The dollar's structural position has been challenged periodically throughout the post-Bretton Woods era. In the 1970s, the collapse of the gold standard and the oil shocks created genuine uncertainty about the dollar's future. In the early 2000s, the twin deficits — fiscal and current account — prompted predictions of a dollar collapse that never materialised. And in recent years, the weaponisation of the dollar through sanctions has accelerated efforts by several major economies to reduce their dependence on dollar-denominated trade and finance.
            </p>
            <p>
              None of these challenges has dislodged the dollar from its dominant position. The dollar's share of global foreign exchange reserves has declined from approximately 72% in 2000 to around 58% today — a meaningful shift, but still a commanding majority. The dollar remains the currency of choice for international trade invoicing, cross-border lending, and global financial transactions. Its dominance is not absolute, but it is durable.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              Why the Dollar May Weaken Structurally
            </h2>
            <p className="mb-4">
              Despite the dollar's structural durability, there are compelling reasons to expect a period of relative weakness over the medium term. The most important is the interest rate differential. The Fed's easing cycle, combined with still-elevated rates in several other major economies, is narrowing the yield advantage that has supported dollar strength since 2022. As this differential compresses, the carry trade that has attracted capital to dollar assets becomes less attractive.
            </p>
            <p className="mb-4">
              Second, the US fiscal position is deteriorating. The Congressional Budget Office projects federal deficits of 5–7% of GDP for the foreseeable future, driven by mandatory spending on Social Security, Medicare, and interest on the national debt. A persistently large fiscal deficit, financed by issuing dollar-denominated debt, creates structural supply pressure on the dollar over time.
            </p>
            <p className="mb-4">
              Third, the geopolitical fragmentation of the global economy is creating incentives for non-dollar trade and finance. The BRICS+ grouping, which now includes major commodity producers such as Saudi Arabia, the UAE, and Iran, has explicitly discussed alternatives to dollar-denominated commodity pricing. While a wholesale shift away from the petrodollar is unlikely in the near term, even a marginal reduction in dollar demand has implications for the currency's valuation.
            </p>
            <p>
              We are not predicting a dollar collapse — that is not our base case. But we do believe the conditions are in place for a multi-year period of dollar underperformance relative to a basket of emerging market currencies, particularly those backed by commodity exporters with strong fiscal positions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              The Emerging Market Opportunity Set
            </h2>
            <p className="mb-4">
              Emerging market equities have underperformed developed markets significantly over the past decade, driven by dollar strength, China's structural slowdown, and a series of idiosyncratic crises in individual EM economies. As a result, EM valuations are at historically attractive levels relative to developed markets. The MSCI Emerging Markets Index trades at approximately 12x forward earnings, compared to 21x for the MSCI World — a discount of nearly 40%.
            </p>
            <p className="mb-4">
              This valuation gap does not automatically translate into outperformance — cheap assets can get cheaper. But combined with the structural tailwinds of dollar weakness, falling US rates, and improving EM fundamentals in several key markets, it creates a compelling medium-term opportunity for selective investors.
            </p>
            <p className="mb-4">
              We are particularly constructive on India, which combines strong domestic demand growth, a young and growing workforce, improving infrastructure, and a government committed to fiscal consolidation. India's equity market has re-rated significantly over the past decade, and valuations are no longer cheap in absolute terms. But the quality of the underlying growth story justifies a premium, and we expect India to be one of the best-performing major equity markets over the next five years.
            </p>
            <p>
              We are also selectively constructive on Southeast Asia — particularly Indonesia and Vietnam — where demographic tailwinds, manufacturing diversification away from China, and improving governance are creating a new generation of high-quality businesses. These markets are less liquid and less well-covered than India, which creates opportunities for patient, research-intensive investors.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              Commodity Markets and the Dollar Link
            </h2>
            <p className="mb-4">
              The relationship between the dollar and commodity prices is one of the most reliable in global markets. Because most commodities are priced in dollars, a weaker dollar mechanically increases the purchasing power of non-dollar buyers, supporting demand. Simultaneously, a weaker dollar reduces the real cost of production for commodity producers outside the US, improving their margins and incentivising supply investment.
            </p>
            <p className="mb-4">
              In a structurally weaker dollar environment, we expect commodities — particularly industrial metals and energy — to perform well. The energy transition is creating structural demand for copper, lithium, nickel, and other critical minerals that will persist for decades. And the underinvestment in conventional energy supply over the past decade, driven by ESG pressures and capital discipline, creates a tight supply backdrop for oil and gas that is unlikely to resolve quickly.
            </p>
            <p>
              For investors, this creates an opportunity to gain exposure to the commodity cycle through high-quality resource companies — businesses with low-cost assets, strong balance sheets, and disciplined capital allocation. These companies have historically been among the best-performing assets in periods of dollar weakness and commodity strength.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              Risks and Mitigants
            </h2>
            <p className="mb-4">
              The EM opportunity is real, but so are the risks. The most significant is a re-acceleration of US inflation, which would force the Fed to pause or reverse its easing cycle, strengthening the dollar and tightening EM financial conditions. This is not our base case, but it is a tail risk that we monitor closely.
            </p>
            <p>
              A second risk is China. China's structural slowdown — driven by a property sector deleveraging, demographic headwinds, and geopolitical tensions — has been a significant drag on EM performance. We are cautious on China-exposed assets and prefer EM markets with limited China linkage. A third risk is political — many EM economies have fragile institutions and are vulnerable to policy reversals that can rapidly undermine investor confidence. We mitigate this through rigorous country selection and a focus on markets with improving governance trajectories.
            </p>
          </section>

        </div>

        <div className="mt-16 pt-8" style={{ borderTop: '1px solid var(--border)' }}>
          <p className="text-xs mb-6" style={{ color: 'var(--muted-foreground)' }}>
            This article is for informational purposes only and does not constitute investment advice. Past performance is not indicative of future results. For qualified investors only.
          </p>
          <Link
            href="/#insights"
            className="inline-flex items-center gap-2 text-sm font-semibold"
            style={{ color: 'var(--primary)' }}
          >
            <ArrowLeft size={14} />
            Back to all perspectives
          </Link>
        </div>
      </article>
    </div>
  );
}
